import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Spinner,
  Table,
  Badge,
  Modal,
  Form,
} from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import { FaArrowLeft, FaExchangeAlt, FaSync, FaCheck } from 'react-icons/fa';
import { MdArrowRight } from 'react-icons/md';
import { procesosAlimentacionApi } from '../../../services/api/procesosAlimentacionApi';
import { turnoApi } from '../../../services/api/turnoApi';
import { fechaTurnoToSortKey, dateTimeToSortKey } from '../../../utils/dateUtils';
import 'react-toastify/dist/ReactToastify.css';

const formatFechaTurno = (fechaTurno) => {
  if (!fechaTurno) return '—';
  const [year, month, day] = String(fechaTurno).split('T')[0].split('-');
  return `${day}/${month}/${year}`;
};

const camposTurno = (t) => ({
  comedor: t.comedor?.nombre || t.comedor_nombre || '—',
  servicio: t.tipo_servicio_alimentacion?.descripcion || t.tipo_servicio_nombre || '—',
  lector: t.lectora?.nombre || t.lector_nombre || '—',
  fecha: formatFechaTurno(t.fecha_turno),
  cantidad: t.cantidad_comensales ?? 0,
});

const descripcionTurno = (t) => {
  if (!t) return null;
  const c = camposTurno(t);
  return {
    linea: `${c.comedor} · ${c.servicio} · ${c.fecha} · ${c.lector}`,
    detalle: c,
  };
};

const resumenTurno = (t) => {
  if (!t) return 'Sin seleccionar — pulse el botón para elegir turno';
  const c = camposTurno(t);
  return `${c.comedor} · ${c.servicio} · ${c.fecha} · Lector: ${c.lector} · ${c.cantidad} comensales`;
};

const resumenTurnoDesdeApi = (r) => {
  if (!r) return '—';
  const fecha = formatFechaTurno(r.fecha_turno);
  return `${r.comedor_nombre || '—'} · ${r.tipo_servicio_nombre || '—'} · ${fecha} · ${r.lector_nombre || '—'}`;
};

const ModalSeleccionTurno = ({
  show,
  onHide,
  titulo,
  turnos,
  loading,
  filtroFecha,
  onFiltroFechaChange,
  onRecargar,
  turnoSeleccionadoId,
  onConfirmar,
  idsExcluir = [],
}) => {
  const [tempId, setTempId] = useState(null);

  useEffect(() => {
    if (show) {
      setTempId(turnoSeleccionadoId ? parseInt(turnoSeleccionadoId, 10) : null);
    }
  }, [show, turnoSeleccionadoId]);

  const turnosFiltrados = useMemo(() => {
    return turnos.filter((t) => !idsExcluir.includes(t.id) || t.id === tempId);
  }, [turnos, idsExcluir, tempId]);

  const handleConfirmar = () => {
    if (!tempId) {
      toast.warning('Seleccione un turno de la lista.');
      return;
    }
    const turno = turnos.find((t) => t.id === tempId);
    onConfirmar(turno);
    onHide();
  };

  const turnoPreview = tempId ? turnos.find((t) => t.id === tempId) : null;
  const preview = descripcionTurno(turnoPreview);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title>{titulo}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
          <Form.Group>
            <Form.Label className="small fw-semibold mb-1">Filtrar por fecha</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={filtroFecha}
              onChange={(e) => onFiltroFechaChange(e.target.value)}
            />
          </Form.Group>
          <Button size="sm" variant="outline-secondary" onClick={onRecargar} disabled={loading}>
            {loading ? <Spinner size="sm" /> : <FaSync />}
          </Button>
        </div>
        <div className="table-responsive">
          <Table hover size="sm" className="mb-0 align-middle table-dh-modal">
            <thead className="table-light">
              <tr>
                <th style={{ width: 48, textAlign: 'center' }}>
                  <MdArrowRight />
                </th>
                <th>Comedor</th>
                <th>Tipo servicio</th>
                <th>Lectora</th>
                <th>Fecha</th>
                <th>Cant.</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="text-center py-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Cargando turnos…
                  </td>
                </tr>
              )}
              {!loading && turnosFiltrados.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    No hay turnos disponibles.
                  </td>
                </tr>
              )}
              {!loading &&
                turnosFiltrados.map((t) => (
                  <tr
                    key={t.id}
                    className={tempId === t.id ? 'table-primary' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setTempId(t.id)}
                  >
                    <td className="text-center" onClick={(e) => e.stopPropagation()}>
                      <Form.Check
                        type="radio"
                        name="turno-modal"
                        checked={tempId === t.id}
                        onChange={() => setTempId(t.id)}
                        aria-label={`Turno ${t.id}`}
                      />
                    </td>
                    <td>{t.comedor?.nombre || '—'}</td>
                    <td>{t.tipo_servicio_alimentacion?.descripcion || '—'}</td>
                    <td>{t.lectora?.nombre || '—'}</td>
                    <td>{formatFechaTurno(t.fecha_turno)}</td>
                    <td>{t.cantidad_comensales}</td>
                    <td>
                      <span
                        className={
                          t.cerrado == 0 || t.cerrado === '0'
                            ? 'text-success fw-semibold'
                            : 'text-secondary'
                        }
                      >
                        {t.cerrado == 0 || t.cerrado === '0' ? 'Abierto' : 'Cerrado'}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
      {preview && (
        <div className="px-3 pb-2">
          <Alert variant="light" className="mb-0 small border">
            <strong>Turno seleccionado:</strong>
            <div className="mt-1">
              <div>
                <strong>Comedor:</strong> {preview.detalle.comedor}
              </div>
              <div>
                <strong>Servicio:</strong> {preview.detalle.servicio}
              </div>
              <div>
                <strong>Fecha:</strong> {preview.detalle.fecha} · <strong>Lector:</strong>{' '}
                {preview.detalle.lector} · <strong>Comensales:</strong> {preview.detalle.cantidad}
              </div>
            </div>
          </Alert>
        </div>
      )}
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button className="btn-dh-primary" onClick={handleConfirmar} disabled={!tempId}>
          <FaCheck className="me-1" />
          Confirmar selección
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const TrasladoTurnosDetalleServicio = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || 'null');
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || 'null');

  const [turnos, setTurnos] = useState([]);
  const [loadingTurnos, setLoadingTurnos] = useState(false);
  const [filtroFecha, setFiltroFecha] = useState('');

  const [turnoOrigen, setTurnoOrigen] = useState(null);
  const [turnoDestino, setTurnoDestino] = useState(null);
  const [turnoReemplazo, setTurnoReemplazo] = useState(null);

  const [modalRol, setModalRol] = useState(null);

  const [loading, setLoading] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const [analisis, setAnalisis] = useState(null);
  const [candidatos, setCandidatos] = useState([]);
  const [sinReemplazo, setSinReemplazo] = useState([]);
  const [seleccionados, setSeleccionados] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [revirtiendo, setRevirtiendo] = useState(null);

  useEffect(() => {
    if (!token || !currentUser) navigate('/');
  }, [navigate, token, currentUser]);

  const cargarHistorial = useCallback(async () => {
    if (!selectedProject?.id) return;
    setLoadingHistorial(true);
    try {
      const res = await procesosAlimentacionApi.historialTrasladoTurnosDetalle(20);
      setHistorial(res.data?.operaciones || []);
    } catch {
      setHistorial([]);
    } finally {
      setLoadingHistorial(false);
    }
  }, [selectedProject?.id]);

  useEffect(() => {
    if (selectedProject?.id) cargarHistorial();
  }, [selectedProject?.id, cargarHistorial]);

  const cargarTurnos = useCallback(async () => {
    if (!selectedProject?.id) return [];
    setLoadingTurnos(true);
    try {
      const filters = { idProyecto: selectedProject.id };
      if (filtroFecha) filters.fechaTurno = filtroFecha;
      const res = await turnoApi.getTurnosTotal(filters);
      const list = (res.data || []).sort((a, b) => {
        const fa = fechaTurnoToSortKey(a.fecha_turno);
        const fb = fechaTurnoToSortKey(b.fecha_turno);
        if (fb !== fa) return fb - fa;
        return dateTimeToSortKey(b.created_at) - dateTimeToSortKey(a.created_at);
      });
      setTurnos(list);
      return list;
    } catch (e) {
      toast.error('No se pudieron cargar los turnos del proyecto.');
      setTurnos([]);
      return [];
    } finally {
      setLoadingTurnos(false);
    }
  }, [selectedProject?.id, filtroFecha]);

  const abrirModalRol = (rol) => {
    if (!selectedProject?.id) {
      toast.error('Seleccione un proyecto en la barra superior.');
      return;
    }
    setModalRol(rol);
  };

  useEffect(() => {
    if (modalRol && selectedProject?.id) {
      cargarTurnos();
    }
  }, [modalRol, filtroFecha, selectedProject?.id, cargarTurnos]);

  const idsExcluirParaModal = useMemo(() => {
    const ids = [];
    if (modalRol !== 'origen' && turnoOrigen?.id) ids.push(turnoOrigen.id);
    if (modalRol !== 'destino' && turnoDestino?.id) ids.push(turnoDestino.id);
    if (modalRol !== 'reemplazo' && turnoReemplazo?.id) ids.push(turnoReemplazo.id);
    return ids;
  }, [modalRol, turnoOrigen, turnoDestino, turnoReemplazo]);

  const confirmarTurnoModal = (turno) => {
    if (modalRol === 'origen') setTurnoOrigen(turno);
    if (modalRol === 'destino') setTurnoDestino(turno);
    if (modalRol === 'reemplazo') setTurnoReemplazo(turno);
    setAnalisis(null);
    setCandidatos([]);
    setSinReemplazo([]);
  };

  const analizar = async () => {
    if (!turnoOrigen || !turnoDestino || !turnoReemplazo) {
      toast.warning('Seleccione los tres turnos: origen, destino y reemplazo.');
      return;
    }
    const ids = [turnoOrigen.id, turnoDestino.id, turnoReemplazo.id];
    if (new Set(ids).size < 3) {
      toast.warning('Los tres turnos deben ser distintos.');
      return;
    }
    setLoading(true);
    setAnalisis(null);
    setCandidatos([]);
    setSinReemplazo([]);
    setSeleccionados({});
    try {
      const res = await procesosAlimentacionApi.analizarTrasladoTurnosDetalle({
        id_turno_cabecera_origen: turnoOrigen.id,
        id_turno_cabecera_destino: turnoDestino.id,
        id_turno_cabecera_reemplazo: turnoReemplazo.id,
      });
      const d = res.data;
      if (d.success) {
        setAnalisis(d);
        setCandidatos(d.candidatos_traslado || []);
        setSinReemplazo(d.sin_registro_en_reemplazo || []);
        const sel = {};
        (d.candidatos_traslado || []).forEach((c) => {
          sel[c.id_turno_detalle] = true;
        });
        setSeleccionados(sel);
        if (!(d.candidatos_traslado || []).length) {
          toast.info('No hay registros en el turno reemplazo para los comensales faltantes.');
        } else {
          toast.success(`${d.candidatos_traslado.length} registro(s) listo(s) para trasladar.`);
        }
      } else {
        toast.error(d.message || 'Error en el análisis.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Error al analizar.');
    } finally {
      setLoading(false);
    }
  };

  const idsSeleccionados = useMemo(
    () =>
      Object.entries(seleccionados)
        .filter(([, v]) => v)
        .map(([k]) => parseInt(k, 10)),
    [seleccionados]
  );

  const confirmarEjecutar = async () => {
    setEjecutando(true);
    setShowConfirm(false);
    try {
      const res = await procesosAlimentacionApi.ejecutarTrasladoTurnosDetalle({
        id_turno_cabecera_origen: turnoOrigen?.id,
        id_turno_cabecera_destino: turnoDestino.id,
        id_turno_cabecera_reemplazo: turnoReemplazo.id,
        ids_turnos_detalle: idsSeleccionados,
      });
      if (res.data?.success) {
        toast.success(res.data.message);
        await cargarHistorial();
        await analizar();
      } else {
        toast.error(res.data?.message || 'No se pudo ejecutar el traslado.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Error al trasladar.');
    } finally {
      setEjecutando(false);
    }
  };

  const revertirOperacion = async (idOperacion) => {
    if (!window.confirm('¿Revertir esta operación? Los registros volverán al turno reemplazo.')) {
      return;
    }
    setRevirtiendo(idOperacion);
    try {
      const res = await procesosAlimentacionApi.revertirTrasladoTurnosDetalle(idOperacion);
      if (res.data?.success) {
        toast.success(res.data.message);
        await cargarHistorial();
        if (turnoOrigen && turnoDestino && turnoReemplazo) await analizar();
      } else {
        toast.error(res.data?.message || 'No se pudo revertir.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Error al revertir.');
    } finally {
      setRevirtiendo(null);
    }
  };

  const toggleTodos = (valor) => {
    const next = {};
    candidatos.forEach((c) => {
      next[c.id_turno_detalle] = valor;
    });
    setSeleccionados(next);
  };

  const modalTitulos = {
    origen: 'Seleccionar turno cabecera — Origen (referencia)',
    destino: 'Seleccionar turno cabecera — Destino (correcto)',
    reemplazo: 'Seleccionar turno cabecera — Reemplazo (registro erróneo)',
  };

  const turnoIdPorRol = {
    origen: turnoOrigen?.id,
    destino: turnoDestino?.id,
    reemplazo: turnoReemplazo?.id,
  };

  return (
    <>
      <style>{`
        .dh-reporte-bg { background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); min-height: 100vh; }
        .dh-main-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white; padding: 2rem 0; margin-bottom: 2rem; border-radius: 0 0 30px 30px;
        }
        .dh-main-title { font-size: 2rem; font-weight: 700; margin: 0; }
        .dh-main-subtitle { font-size: 1rem; opacity: 0.9; margin-top: 0.5rem; }
        .dh-card { border: none; border-radius: 20px; box-shadow: 0 15px 35px rgba(0,0,0,0.08); }
        .dh-card-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white; border-radius: 20px 20px 0 0; font-weight: 700;
        }
        .btn-dh-primary {
          background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
          border: none; color: white; border-radius: 12px; font-weight: 600;
        }
        .btn-dh-secondary {
          background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
          border: none; color: white; border-radius: 12px;
        }
        .dh-alert-info { background: #eef2ff; border: 1px solid #c7d2fe; border-radius: 12px; color: #3730a3; }
        .turno-selector-card {
          border: 2px solid #e5e7eb; border-radius: 16px; padding: 1.25rem;
          height: 100%; background: #fff; transition: border-color 0.2s;
        }
        .turno-selector-card.has-turno { border-color: #a5b4fc; background: #f8faff; }
        .turno-selector-card .resumen { font-size: 0.85rem; color: #374151; min-height: 2.5rem; }
        .table-dh-modal tbody tr.table-primary { --bs-table-bg: #e0e7ff; }
      `}</style>

      <div className="dh-reporte-bg">
        <ToastContainer position="top-right" />
        <div className="dh-main-header">
          <Container>
            <Row className="align-items-center">
              <Col>
                <h1 className="dh-main-title">
                  <FaExchangeAlt className="me-2" />
                  Traslado de atenciones entre turnos
                </h1>
                <p className="dh-main-subtitle mb-0">
                  Elija origen, destino y reemplazo. Analice y traslade los registros al turno correcto.
                </p>
              </Col>
              <Col xs="auto">
                <Button className="btn-dh-secondary" onClick={() => navigate(-1)}>
                  <FaArrowLeft className="me-2" />
                  Volver
                </Button>
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="pb-5">
          <Card className="dh-card mb-4">
            <Card.Header className="dh-card-header">Selección de turnos</Card.Header>
            <Card.Body>
              <Row className="g-3 mb-4">
                <Col md={4}>
                  <div className={`turno-selector-card ${turnoOrigen ? 'has-turno' : ''}`}>
                    <div className="mb-2">
                      <Badge bg="primary">Origen</Badge>
                    </div>
                    <p className="resumen mb-3">{resumenTurno(turnoOrigen)}</p>
                    <Button
                      variant="primary"
                      className="w-100 rounded-3"
                      onClick={() => abrirModalRol('origen')}
                    >
                      {turnoOrigen ? 'Cambiar origen' : 'Seleccionar origen'}
                    </Button>
                  </div>
                </Col>
                <Col md={4}>
                  <div className={`turno-selector-card ${turnoDestino ? 'has-turno' : ''}`}>
                    <div className="mb-2">
                      <Badge bg="success">Destino</Badge>
                    </div>
                    <p className="resumen mb-3">{resumenTurno(turnoDestino)}</p>
                    <Button
                      variant="success"
                      className="w-100 rounded-3"
                      onClick={() => abrirModalRol('destino')}
                    >
                      {turnoDestino ? 'Cambiar destino' : 'Seleccionar destino'}
                    </Button>
                  </div>
                </Col>
                <Col md={4}>
                  <div className={`turno-selector-card ${turnoReemplazo ? 'has-turno' : ''}`}>
                    <div className="mb-2">
                      <Badge bg="warning" text="dark">
                        Reemplazo
                      </Badge>
                    </div>
                    <p className="resumen mb-3">{resumenTurno(turnoReemplazo)}</p>
                    <Button
                      variant="warning"
                      className="w-100 rounded-3"
                      onClick={() => abrirModalRol('reemplazo')}
                    >
                      {turnoReemplazo ? 'Cambiar reemplazo' : 'Seleccionar reemplazo'}
                    </Button>
                  </div>
                </Col>
              </Row>

              <div className="text-center">
                <Button
                  className="btn-dh-primary px-5"
                  onClick={analizar}
                  disabled={loading || !turnoOrigen || !turnoDestino || !turnoReemplazo}
                >
                  {loading ? <Spinner size="sm" className="me-2" /> : null}
                  Analizar diferencias
                </Button>
              </div>

              <Alert variant="info" className="dh-alert-info mt-3 mb-0 small">
                <strong>Origen:</strong> turno de referencia (ej. ayer).{' '}
                <strong>Destino:</strong> turno correcto donde debieron registrarse.{' '}
                <strong>Reemplazo:</strong> turno donde se cargaron por error.
              </Alert>
            </Card.Body>
          </Card>

          {analisis && (
            <Card className="dh-card mb-4">
              <Card.Header className="dh-card-header">Resumen del análisis</Card.Header>
              <Card.Body>
                <Badge bg="secondary" className="me-2">
                  Origen: {analisis.referencia?.total_comensales_origen} comensales
                </Badge>
                <Badge bg="primary" className="me-2">
                  Destino: {analisis.referencia?.total_comensales_destino} comensales
                </Badge>
                <Badge bg="warning" text="dark" className="me-2">
                  Faltan en destino: {analisis.referencia?.total_faltantes_en_destino}
                </Badge>
                <Badge bg="info" text="dark">
                  En reemplazo: {analisis.total_candidatos_traslado}
                </Badge>
              </Card.Body>
            </Card>
          )}

          {candidatos.length > 0 && (
            <Card className="dh-card mb-4">
              <Card.Header className="dh-card-header d-flex justify-content-between align-items-center">
                <span>Trasladar al destino ({candidatos.length})</span>
                <div className="d-flex gap-2">
                  <Button size="sm" variant="light" onClick={() => toggleTodos(true)}>
                    Todos
                  </Button>
                  <Button size="sm" variant="light" onClick={() => toggleTodos(false)}>
                    Ninguno
                  </Button>
                  <Button
                    className="btn-dh-primary btn-sm"
                    disabled={ejecutando || idsSeleccionados.length === 0}
                    onClick={() => setShowConfirm(true)}
                  >
                    {ejecutando ? <Spinner size="sm" /> : 'Trasladar al destino'}
                  </Button>
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                <Table striped bordered size="sm" className="mb-0 small">
                  <thead className="table-light">
                    <tr>
                      <th />
                      <th>ID detalle</th>
                      <th>Documento</th>
                      <th>Comensal</th>
                      <th>Cliente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidatos.map((c) => (
                      <tr key={c.id_turno_detalle}>
                        <td>
                          <Form.Check
                            checked={!!seleccionados[c.id_turno_detalle]}
                            onChange={(e) =>
                              setSeleccionados((prev) => ({
                                ...prev,
                                [c.id_turno_detalle]: e.target.checked,
                              }))
                            }
                          />
                        </td>
                        <td>{c.id_turno_detalle}</td>
                        <td>{c.numero_documento_identidad || '—'}</td>
                        <td>{c.nombre_comensal}</td>
                        <td>{c.nombre_cliente || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {sinReemplazo.length > 0 && (
            <Card className="dh-card mb-4">
              <Card.Header className="dh-card-header">
                Faltantes en destino sin registro en turno reemplazo
              </Card.Header>
              <Card.Body className="p-0">
                <Table size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Documento</th>
                      <th>Comensal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sinReemplazo.map((s) => (
                      <tr key={s.id_comensal}>
                        <td>{s.numero_documento_identidad || '—'}</td>
                        <td>{s.nombre_comensal}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          <Card className="dh-card mb-4">
            <Card.Header className="dh-card-header d-flex justify-content-between align-items-center">
              <span>Historial de traslados (auditoría)</span>
              <Button size="sm" variant="light" onClick={cargarHistorial} disabled={loadingHistorial}>
                {loadingHistorial ? <Spinner size="sm" /> : <FaSync />}
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              {loadingHistorial && historial.length === 0 ? (
                <div className="text-center py-4">
                  <Spinner size="sm" className="me-2" />
                  Cargando historial…
                </div>
              ) : historial.length === 0 ? (
                <p className="text-muted text-center py-4 mb-0 small">
                  No hay operaciones registradas. Ejecute la migración SQL de auditoría si acaba de
                  implementar esta función.
                </p>
              ) : (
                <Table striped size="sm" className="mb-0 small">
                  <thead className="table-light">
                    <tr>
                      <th>Fecha</th>
                      <th>Desde (reemplazo)</th>
                      <th>Hacia (destino)</th>
                      <th>Cant.</th>
                      <th>Estado</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {historial.map((op) => (
                      <tr key={op.id}>
                        <td>{op.ejecutado_en ? String(op.ejecutado_en).replace('T', ' ').slice(0, 16) : '—'}</td>
                        <td>{resumenTurnoDesdeApi(op.resumen_turno_reemplazo)}</td>
                        <td>{resumenTurnoDesdeApi(op.resumen_turno_destino)}</td>
                        <td>{op.total_trasladados}</td>
                        <td>
                          <Badge bg={op.estado === 'revertido' ? 'secondary' : 'success'}>
                            {op.estado === 'revertido' ? 'Revertido' : 'Ejecutado'}
                          </Badge>
                        </td>
                        <td>
                          {op.estado !== 'revertido' && (
                            <Button
                              size="sm"
                              variant="outline-danger"
                              disabled={revirtiendo === op.id}
                              onClick={() => revertirOperacion(op.id)}
                            >
                              {revirtiendo === op.id ? <Spinner size="sm" /> : 'Revertir'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Container>

        {modalRol && (
          <ModalSeleccionTurno
            show={!!modalRol}
            onHide={() => setModalRol(null)}
            titulo={modalTitulos[modalRol]}
            turnos={turnos}
            loading={loadingTurnos}
            filtroFecha={filtroFecha}
            onFiltroFechaChange={setFiltroFecha}
            onRecargar={cargarTurnos}
            turnoSeleccionadoId={turnoIdPorRol[modalRol]}
            onConfirmar={confirmarTurnoModal}
            idsExcluir={idsExcluirParaModal}
          />
        )}

        <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Confirmar traslado</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-3">
              Se moverán <strong>{idsSeleccionados.length}</strong> registro(s) de comensales al
              turno destino correcto:
            </p>
            <Table bordered size="sm" className="mb-0">
              <tbody>
                <tr>
                  <th className="bg-light" style={{ width: '28%' }}>
                    Desde (reemplazo)
                  </th>
                  <td>{resumenTurno(turnoReemplazo)}</td>
                </tr>
                <tr>
                  <th className="bg-light">Hacia (destino)</th>
                  <td>{resumenTurno(turnoDestino)}</td>
                </tr>
                {turnoOrigen && (
                  <tr>
                    <th className="bg-light">Referencia (origen)</th>
                    <td>{resumenTurno(turnoOrigen)}</td>
                  </tr>
                )}
              </tbody>
            </Table>
            <Alert variant="info" className="dh-alert-info mt-3 mb-0 small">
              La operación quedará registrada en el historial y podrá revertirse si hubiera un error.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfirm(false)}>
              Cancelar
            </Button>
            <Button className="btn-dh-primary" onClick={confirmarEjecutar}>
              Confirmar
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default TrasladoTurnosDetalleServicio;
